/**
 * TelegramAuthProvider - Sets auth header before children render
 *
 * Uses window.Telegram.WebApp.initData directly (available synchronously
 * from telegram-web-app.js loaded in <head>) instead of SDK async calls.
 *
 * Note: telegram-web-app.js creates WebApp object even in regular browsers,
 * but initData will be empty. We detect "in Telegram" by having non-empty
 * initData, not by WebApp object existence.
 */

import { type ReactNode, useEffect } from 'react';
import { api } from '../api';

interface TelegramAuthProviderProps {
  children: ReactNode;
}

// Track if auth has been set (module-level to persist across renders)
let hasSetAuth = false;

/**
 * Get Telegram init data from native WebApp API
 * Returns null if not in Telegram environment (or empty initData)
 */
function getTelegramInitData(): string | null {
  if (typeof window !== 'undefined' && window.Telegram?.WebApp?.initData) {
    return window.Telegram.WebApp.initData;
  }
  return null;
}

// Set auth header synchronously on module load (before any component renders)
// This ensures the header is set before any API calls
if (!hasSetAuth) {
  const initData = getTelegramInitData();
  if (initData) {
    api.setInitData(initData);
    hasSetAuth = true;
  }
}

export function TelegramAuthProvider({ children }: TelegramAuthProviderProps) {
  // Also set in useEffect as a fallback in case module initialization missed it
  useEffect(() => {
    if (hasSetAuth) return;

    const initData = getTelegramInitData();
    if (initData) {
      api.setInitData(initData);
      hasSetAuth = true;
    }
  }, []);

  return <>{children}</>;
}

export default TelegramAuthProvider;
